import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import { HpiDateFilterBar, HpiFilterSelect } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import {
  editEquipmentLogPath,
  equipmentLogPath,
  equipmentLogsPath,
  newEquipmentLogPath,
  newExpenseEquipmentLogsPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface EquipmentLogEntry {
  id: number
  equipment_id: number
  equipment_name: string | null
  vendor_name: string | null
  employee_name: string | null
  log_date: string
  end_date: string | null
  amount: number
  billable_amount: number | null
  paid_to: string | null
  paid_by: string | null
  notes: string | null
  start_odometer: number | null
  end_odometer: number | null
  odometer_usage: number | null
  status: number
  approved: boolean
  is_expense: boolean
  has_receipt: boolean
  created_by_name: string | null
  user_id: number | null
  can_edit: boolean
  can_delete: boolean
}

interface FilterOptions {
  equipment: { id: number; name: string }[]
  vendors: { id: number; name: string }[]
  show_types: { value: string; label: string }[]
}

interface Filters {
  equipment_id: string
  vendor_id: string
  start_date: string
  end_date: string
  show: string
}

interface Props {
  logs: EquipmentLogEntry[]
  filter_options: FilterOptions
  filters: Filters
  total_expenses: number
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Equipment", href: "/equipment" },
  { title: "Maintenance Logs", href: "/equipment_logs" },
]

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount)
}

/* ── Filter Bar ────────────────────────────────────────── */

function FilterBar({
  filters,
  filterOptions,
}: {
  filters: Filters
  filterOptions: FilterOptions
}) {
  const [equipmentId, setEquipmentId] = useState(filters.equipment_id)
  const [vendorId, setVendorId] = useState(filters.vendor_id)
  const [startDate, setStartDate] = useState(filters.start_date)
  const [endDate, setEndDate] = useState(filters.end_date)
  const [show, setShow] = useState(filters.show)

  const handleUpdate = () => {
    const params: Record<string, string> = {}
    if (equipmentId) params.equipment_id = equipmentId
    if (vendorId) params.vendor_id = vendorId
    if (startDate) params.start_date = startDate
    if (endDate) params.end_date = endDate
    if (show) params.show = show
    router.get(equipmentLogsPath(), params, { preserveState: true })
  }

  const handleClear = () => {
    setEquipmentId("")
    setVendorId("")
    setStartDate("")
    setEndDate("")
    setShow("")
    router.get(equipmentLogsPath(), {}, { preserveState: true })
  }

  const hasFilters = equipmentId || vendorId || startDate || endDate || show

  return (
    <div className="mb-6">
      <HpiDateFilterBar
        startDate={startDate}
        onStartDateChange={setStartDate}
        endDate={endDate}
        onEndDateChange={setEndDate}
        onUpdate={handleUpdate}
      >
        <HpiFilterSelect
          label="Equipment"
          value={equipmentId}
          onChange={setEquipmentId}
          options={filterOptions.equipment.map((e) => ({
            value: e.id,
            label: e.name,
          }))}
          placeholder="All Equipment"
        />
        <HpiFilterSelect
          label="Vendor"
          value={vendorId}
          onChange={setVendorId}
          options={filterOptions.vendors.map((v) => ({
            value: v.id,
            label: v.name,
          }))}
          placeholder="All Vendors"
        />
        <HpiFilterSelect
          label="Show"
          value={show}
          onChange={setShow}
          options={filterOptions.show_types
            .filter((t) => t.value !== "")
            .map((t) => ({
              value: t.value,
              label: t.label,
            }))}
          placeholder="All"
        />
      </HpiDateFilterBar>
      {hasFilters && (
        <button
          onClick={handleClear}
          className="text-hpi-med-gray hover:text-hpi-dark-gray mt-2 px-4 text-sm transition-colors"
        >
          Clear Filters
        </button>
      )}
    </div>
  )
}

/* ── Expense Table ─────────────────────────────────────── */

function ExpenseTable({
  entries,
  totalExpenses,
}: {
  entries: EquipmentLogEntry[]
  totalExpenses: number
}) {
  const handleDelete = (id: number) => {
    if (!confirm("Are you sure you want to delete this entry?")) return
    router.delete(equipmentLogPath(id), { preserveState: false })
  }

  return (
    <table className="border-hpi-light-gray w-full border-collapse border">
      <thead>
        <tr className="bg-hpi-pale-gray">
          <th className="border-hpi-light-gray border px-4 py-2 text-left">
            Date
          </th>
          <th className="border-hpi-light-gray border px-4 py-2 text-left">
            Equipment
          </th>
          <th className="border-hpi-light-gray border px-4 py-2 text-left">
            Payee
          </th>
          <th className="border-hpi-light-gray border px-4 py-2 text-left">
            Description
          </th>
          <th className="border-hpi-light-gray border px-4 py-2 text-right">
            Amount
          </th>
          <th className="border-hpi-light-gray border px-4 py-2 text-left">
            Actions
          </th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry) => (
          <tr key={entry.id} className="hover:bg-hpi-pale-blue">
            <td className="border-hpi-light-gray border px-4 py-2 whitespace-nowrap">
              {formatDate(entry.log_date)}
            </td>
            <td className="border-hpi-light-gray border px-4 py-2">
              {entry.equipment_name}
            </td>
            <td className="border-hpi-light-gray border px-4 py-2">
              {entry.vendor_name}
            </td>
            <td className="border-hpi-light-gray border px-4 py-2">
              {entry.notes}
            </td>
            <td className="border-hpi-light-gray border px-4 py-2 text-right whitespace-nowrap">
              {formatCurrency(entry.amount)}
            </td>
            <td className="border-hpi-light-gray border px-4 py-2">
              <div className="flex gap-2">
                <Link
                  href={equipmentLogPath(entry.id)}
                  className="text-hpi-active-blue hover:text-hpi-blue text-sm"
                >
                  View
                </Link>
                {entry.can_edit && (
                  <Link
                    href={editEquipmentLogPath(entry.id)}
                    className="text-hpi-active-blue hover:text-hpi-blue text-sm"
                  >
                    Edit
                  </Link>
                )}
                {entry.can_delete && (
                  <button
                    onClick={() => handleDelete(entry.id)}
                    className="text-hpi-red text-sm hover:text-red-700"
                  >
                    Delete
                  </button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="bg-hpi-pale-gray font-semibold">
          <td
            colSpan={4}
            className="border-hpi-light-gray border px-4 py-2 text-right"
          >
            Total
          </td>
          <td className="border-hpi-light-gray border px-4 py-2 text-right whitespace-nowrap">
            {formatCurrency(totalExpenses)}
          </td>
          <td className="border-hpi-light-gray border px-4 py-2" />
        </tr>
      </tfoot>
    </table>
  )
}

/* ── Log List ──────────────────────────────────────────── */

function LogList({ entries }: { entries: EquipmentLogEntry[] }) {
  const handleDelete = (id: number) => {
    if (!confirm("Are you sure you want to delete this entry?")) return
    router.delete(equipmentLogPath(id), { preserveState: false })
  }

  return (
    <ul className="space-y-4">
      {entries.map((entry) => (
        <li key={entry.id} className="border-hpi-light-gray rounded border p-4">
          <div className="mb-2 flex items-start justify-between">
            <div>
              <span className="font-semibold">
                {formatDate(entry.log_date)}
                {entry.end_date && ` - ${formatDate(entry.end_date)}`}
              </span>
              {entry.equipment_name && (
                <span className="text-hpi-med-gray ml-2">
                  {entry.equipment_name}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Link
                href={equipmentLogPath(entry.id)}
                className="text-hpi-active-blue hover:text-hpi-blue text-sm"
              >
                View
              </Link>
              {entry.can_edit && (
                <Link
                  href={editEquipmentLogPath(entry.id)}
                  className="text-hpi-active-blue hover:text-hpi-blue text-sm"
                >
                  Edit
                </Link>
              )}
              {entry.can_delete && (
                <button
                  onClick={() => handleDelete(entry.id)}
                  className="text-hpi-red text-sm hover:text-red-700"
                >
                  Delete
                </button>
              )}
            </div>
          </div>

          {entry.employee_name && (
            <div className="text-sm">
              <span className="text-hpi-med-gray">Employee:</span>{" "}
              {entry.employee_name}
            </div>
          )}

          {entry.vendor_name && (
            <div className="text-sm">
              <span className="text-hpi-med-gray">Vendor:</span>{" "}
              {entry.vendor_name}
            </div>
          )}

          {(entry.start_odometer != null || entry.end_odometer != null) && (
            <div className="text-sm">
              <span className="text-hpi-med-gray">Odometer/Hours:</span>{" "}
              {entry.start_odometer}
              {entry.end_odometer != null && ` - ${entry.end_odometer}`}
              {entry.odometer_usage != null && (
                <span className="text-hpi-med-gray ml-1">
                  ({entry.odometer_usage} usage)
                </span>
              )}
            </div>
          )}

          {entry.notes && (
            <div className="text-hpi-dark-gray mt-2 text-sm whitespace-pre-wrap">
              {entry.notes}
            </div>
          )}

          {entry.amount > 0 && (
            <div className="mt-1 text-sm">
              <span className="text-hpi-med-gray">Amount:</span>{" "}
              {formatCurrency(entry.amount)}
              {entry.paid_by && (
                <span className="text-hpi-med-gray ml-2">
                  ({entry.paid_by})
                </span>
              )}
            </div>
          )}
        </li>
      ))}
    </ul>
  )
}

/* ── Main Component ────────────────────────────────────── */

export default function Index({
  logs,
  filter_options,
  filters,
  total_expenses,
}: Props) {
  const showType = filters.show
  const expenses = logs.filter((l) => l.is_expense)
  const logEntries = logs.filter((l) => !l.is_expense)
  const showExpenses =
    showType === "expenses" || (!showType && expenses.length > 0)
  const showLogs = showType === "logs" || (!showType && logEntries.length > 0)

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Equipment Maintenance Logs" />

      <div className="p-6">
        <FilterBar filters={filters} filterOptions={filter_options} />

        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Equipment Maintenance Logs</h1>
          <div className="flex gap-2">
            <Link
              href={newExpenseEquipmentLogsPath()}
              className="border-hpi-dark-blue text-hpi-dark-blue hover:bg-hpi-pale-blue rounded border px-4 py-2"
            >
              Add Expense
            </Link>
            <Link
              href={newEquipmentLogPath()}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
            >
              Add Log
            </Link>
          </div>
        </div>

        {logs.length === 0 ? (
          <p className="text-hpi-med-gray">No log entries found.</p>
        ) : (
          <div className="space-y-8">
            {showExpenses && expenses.length > 0 && (
              <div>
                {!showType && (
                  <h2 className="mb-3 text-lg font-semibold">Expenses</h2>
                )}
                <ExpenseTable
                  entries={expenses}
                  totalExpenses={total_expenses}
                />
              </div>
            )}

            {showLogs && logEntries.length > 0 && (
              <div>
                {!showType && (
                  <h2 className="mb-3 text-lg font-semibold">
                    Maintenance Logs
                  </h2>
                )}
                <LogList entries={logEntries} />
              </div>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  )
}
